import { Grid, IconButton, InputAdornment } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { validateEmail, validatePhoneNumber } from 'src/common/helpers/validation-helper';
import { IReceiptArgs } from 'src/common/interfaces/dtos/IReceiptArgs';
import { ICustomerState } from 'src/common/interfaces/states/ICustomerState';
import {
  DigitalReceiptDetails,
  DigitalReceiptOptions,
} from 'src/common/interfaces/states/TenderingState';
import { CssTextField } from 'src/css-text-field/css-text-field';
import EmeraldDialog from '../../../common/components/emerald-dialog/emerald-dialog';
import { styles } from './digital-receipt-form-styles';

const DigitalReceiptForm: React.FC<DigitalReceiptFormProps> = (
  props: DigitalReceiptFormProps,
): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  const altIdInput = React.useRef<HTMLInputElement>(null);
  const emailInput = React.useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<DigitalReceiptDetails>({});

  const { transactionCustomer, digitalReceiptOptions, isAlsoPrintReceipt } = props;

  const showCustomerData = digitalReceiptOptions.showCustomerContactData;

  const [altId, setAltId] = useState(
    (showCustomerData && transactionCustomer && transactionCustomer.mobilePhoneNumber) || '',
  );
  const [email, setEmail] = useState(
    (showCustomerData && transactionCustomer && transactionCustomer.emailAddress) || '',
  );

  const onAccept = (): void => {
    errors.altId = !altId || validatePhoneNumber(altId) ? undefined : 'Valid Phone Number required';

    errors.email = !email || validateEmail(email) ? undefined : 'Valid Email required';
    if (errors.altId || errors.email) {
      setErrors(prevErr => ({ ...prevErr, altId: errors.altId, email: errors.email }));
    } else {
      const digitalReceiptDetails = {
        altId: altId,
        email: email,
      };
      const receiptDetails = props.handleConfirm(isAlsoPrintReceipt, digitalReceiptDetails);
      props.resolveHandler(receiptDetails);
    }
  };

  const onCancel = (): void => {
    props.handleCancel();
  };

  return (
    <EmeraldDialog
      open
      data-testid='digital-receipt-dialog'
      classes={{ paper: classes.dialogPaper }}
    >
      <Grid container direction='column' data-testid='digital-receipt-form'>
        <Grid
          className={classes.header}
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          data-testid='digital-receipt-header'
        >
          <Grid item data-testid='digital-receipt-header-icon'>
            <span className={classes.spacing}>Input Text</span>
          </Grid>
          <Grid>
            <InfoIcon classes={{ root: classes.icon }} />
          </Grid>
        </Grid>
        <Grid item className='flexFull'>
          <Grid container justifyContent='center'>
            <Grid item className={`centerText`}>
              <Grid container className={classes.digitalReceiptForm}>
                <Grid
                  container
                  direction='column'
                  justifyContent='space-between'
                  className={classes.contentContainer}
                >
                  <Grid container direction='column'>
                    <div className={classes.textContainer}>
                      <p className={classes.title} data-testid='digital-receipt-title'>
                        {t('CustomerReceiptDetails')}
                      </p>
                      <p className={classes.message} data-testid='digital-receipt-message'>
                        {t('FillInFormWithOk')}
                      </p>
                    </div>
                    {digitalReceiptOptions.phoneRequested && (
                      <Grid item className={classes.inputGridItem} key={'altId'}>
                        <RenderTextField
                          label='Phone'
                          inputRef={altIdInput}
                          propSetter={(input: string) => setAltId(input)}
                          keyName='altId'
                          value={altId}
                          errorMessage={errors.altId}
                          autoFocus={!digitalReceiptOptions.emailRequested || (!altId && !!email)}
                        />
                      </Grid>
                    )}
                    {digitalReceiptOptions.emailRequested && (
                      <Grid item className={classes.inputGridItem} key={'email'}>
                        <RenderTextField
                          label='Email'
                          inputRef={emailInput}
                          propSetter={(input: string) => {
                            setEmail(input);
                          }}
                          keyName='email'
                          value={email}
                          errorMessage={errors.email}
                          autoFocus={!digitalReceiptOptions.phoneRequested || !email || !!altId}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Grid container justifyContent='flex-end'>
                    <Grid item>
                      <BaseButton
                        buttonType='white'
                        data-testid='dr-cancel-button'
                        onClick={onCancel}
                        className={`${classes.button} ${classes.cancelButton}`}
                      >
                        {t('Cancel')}
                      </BaseButton>
                    </Grid>
                    <Grid item>
                      <BaseButton
                        buttonType='primary'
                        data-testid='dr-confirm-button'
                        onClick={onAccept}
                        className={`${classes.button} ${classes.endButton}`}
                      >
                        {t('Ok')}
                      </BaseButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

const RenderTextField = ({
  propSetter,
  label,
  inputRef,
  value,
  keyName,
  errorMessage,
  autoFocus,
}: TexFieldProps): JSX.Element => {
  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const inputValue = event.target.value;
    propSetter(inputValue);
  };

  return (
    <CssTextField
      variant='outlined'
      label={label}
      inputRef={inputRef}
      value={value}
      key={keyName}
      error={!!errorMessage}
      helperText={<span data-testid={`${keyName}-error-message`}>{errorMessage}</span>}
      autoFocus={autoFocus === true}
      autoComplete='off'
      inputProps={{
        'data-testid': `digital-receipt-input-${keyName}`,
        className: 'global-keyboard',
      }}
      onChange={onTextChange}
      InputProps={{
        endAdornment: value ? (
          <InputAdornment position='end'>
            <IconButton
              data-testid={`clear-${keyName}-input`}
              onClick={(): void => {
                propSetter('');
                inputRef.current && inputRef.current.focus();
              }}
              tabIndex={-1}
            >
              <Close />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

interface TexFieldProps {
  label: string;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  propSetter: (input: string) => void;
  keyName: string;
  value?: string;
  errorMessage?: string;
  autoFocus?: boolean;
}

interface DigitalReceiptFormProps {
  digitalReceiptOptions: DigitalReceiptOptions;
  transactionCustomer?: Partial<ICustomerState> | undefined;
  isAlsoPrintReceipt: boolean;
  handleCancel: () => void;
  handleConfirm: (
    isAlsoPrintReceipt: boolean,
    details: Partial<DigitalReceiptDetails>,
  ) => IReceiptArgs;
  resolveHandler: (receiptDetails: IReceiptArgs) => void;
}

export default DigitalReceiptForm;
